import {
  IInvoiceRespSchema,
  ISharedInvoicePaymentUI,
  PaymentGatewayConfigurations,
} from '@monetize/types/app';
import { formatCurrency, getCurrencyDecimals } from '@monetize/utils/core';
import { PaymentElement } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  MBox,
  MCustomNumberInput,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MText,
} from '../../../components/Monetize';
import { StripePaymentMethodTerms } from '../../../components/Payments/StripePaymentMethodTerms';
import {
  getPaymentAmountLimits,
  stripeTypeToPaymentMethodType,
  validatePaymentAmountLimits,
} from '../../../utils/payment.utils';

interface SharedInvoiceFormNewPaymentMethodProps {
  allowPartialPayment: boolean;
  formState: UseFormReturn<ISharedInvoicePaymentUI>;
  invoice: IInvoiceRespSchema;
  isDisabled?: boolean;
  paymentConfiguration?: PaymentGatewayConfigurations;
}

export const SharedInvoiceFormNewPaymentMethod = ({
  allowPartialPayment,
  formState,
  invoice,
  isDisabled,
  paymentConfiguration,
}: SharedInvoiceFormNewPaymentMethodProps) => {
  const {
    setValue,
    control,
    watch,
    formState: { errors },
  } = formState;

  const stripeData = watch('stripeData');
  const amount = watch('amount');
  const { currency } = invoice;

  const amountLimits = useMemo(() => {
    const methodType = stripeTypeToPaymentMethodType(stripeData.type);
    if (!methodType) {
      return null;
    }
    return getPaymentAmountLimits(paymentConfiguration, methodType, currency);
  }, [paymentConfiguration, stripeData.type, currency]);

  const amountLimitError = useMemo(() => {
    if (!amountLimits) {
      return null;
    }
    return validatePaymentAmountLimits(amountLimits, amount, currency);
  }, [amountLimits, amount, currency]);

  const handlePaymentFormChangeEvent = ({
    complete,
    empty,
    value,
  }: StripePaymentElementChangeEvent) => {
    setValue('stripeData', {
      empty,
      complete,
      type: value.type,
    });
  };

  return (
    <MBox>
      <PaymentElement
        onChange={handlePaymentFormChangeEvent}
        options={{
          layout: {
            type: 'accordion',
            radios: true,
            defaultCollapsed: false,
            spacedAccordionItems: true,
          },
          terms: {
            usBankAccount: 'never',
            card: 'never',
          },
          fields: {
            billingDetails: {
              name: 'never',
              email: 'never',
              phone: 'auto',
              address: 'auto',
            },
          },
        }}
      />
      <MGrid templateColumns="repeat(12, 1fr)" gap={4} my={3}>
        <MGridItem colSpan={12}>
          <MFormField
            isDisabled={isDisabled}
            error={
              errors?.amount ||
              (amountLimitError
                ? { message: amountLimitError, type: 'manual' }
                : undefined)
            }
            label="Payment Amount"
            isRequired
          >
            <Controller
              name="amount"
              control={control}
              render={({ field: { onChange, ...field } }) =>
                allowPartialPayment ? (
                  <MCustomNumberInput
                    precision={getCurrencyDecimals(currency)}
                    isDisabled={isDisabled}
                    onChange={(_, valueAsNumber) => onChange(valueAsNumber)}
                    {...field}
                  />
                ) : (
                  <MText>
                    {formatCurrency(field.value, {
                      currency: invoice.currency,
                    })}
                  </MText>
                )
              }
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={12}>
          <MFormField
            isDisabled={isDisabled}
            error={errors?.billingDetails?.fullName}
            label="Full Name"
            isRequired
          >
            <Controller
              name="billingDetails.fullName"
              control={control}
              defaultValue=""
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={12}>
          <MFormField
            isDisabled={isDisabled}
            error={errors.billingDetails?.email}
            label="Email"
            isRequired
          >
            <Controller
              name="billingDetails.email"
              control={control}
              defaultValue=""
              render={({ field }) => <MInput {...field} />}
            />
          </MFormField>
        </MGridItem>
        <MGridItem colSpan={12}>
          <StripePaymentMethodTerms
            companyName={invoice.fromCompany.name || ''}
            type={stripeData.type}
          />
        </MGridItem>
      </MGrid>
    </MBox>
  );
};
